import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Check, Sparkles, Zap, Users } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const ICONS = { free: Users, pro: Zap, vip: Sparkles, agency: Sparkles };

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [mine, setMine] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState(null);
  useDocumentTitle("Plans");

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/plans"), api.get("/subscriptions/mine")]);
    setPlans(p.data || []);
    setMine(s.data || null);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/subscriptions", { plan_key: planKey, period });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success(`Now on ${planKey}`); load(); }
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); } finally { setBusy(null); }
  };

  const cancel = async () => {
    if (!mine?.id) return;
    if (!window.confirm(`Cancel your ${mine.plan_key} subscription at period end?`)) return;
    try { await api.post(`/subscriptions/${mine.id}/cancel`); toast.success("Canceled"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-5xl" data-testid="plans-page">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider · Plans</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Grow with the right plan.</h1>
          {mine?.plan_key && mine.plan_key !== "free" && (
            <p className="text-sm text-[#252A34]/70 mt-1">Currently on <b>{mine.plan_key}</b> ({mine.period}) · renews {mine.renews_at ? new Date(mine.renews_at).toLocaleDateString() : "—"}. <button onClick={cancel} className="underline text-rose-600" data-testid="plan-cancel">Cancel</button></p>
          )}
        </div>

        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 mb-6">
          {["monthly", "annual"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-1.5 text-sm rounded-full ${period === p ? "bg-[#165a71] text-white" : "text-[#252A34]/80"}`} data-testid={`plans-period-${p}`}>{p}</button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((p) => {
            const Icon = ICONS[p.key] || CreditCard;
            const active = mine?.plan_key === p.key;
            const price = period === "annual" ? p.annual_price : p.monthly_price;
            return (
              <div key={p.id} className={`rounded-2xl border ${active ? "border-[#165a71] shadow-lg" : "border-slate-200"} bg-white p-6`} data-testid={`plan-card-${p.key}`}>
                <div className="flex items-center gap-2 text-[#165a71]"><Icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wider font-semibold">{p.key}</span></div>
                <div className="font-display text-2xl font-semibold text-[#071b22] mt-2">{p.name}</div>
                <div className="mt-2 font-display text-3xl font-bold text-[#071b22]">${price || 0}<span className="text-sm text-[#252A34]/60 font-normal">/{period === "annual" ? "yr" : "mo"}</span></div>
                <ul className="mt-4 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> {p.listing_limit ? `${p.listing_limit} listings` : "Unlimited listings"}</li>
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> {p.team_member_limit ? `Up to ${p.team_member_limit} team members` : "Solo"}</li>
                  {p.transaction_fee_override != null && <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Reduced platform fee: {(p.transaction_fee_override * 100).toFixed(0)}%</li>}
                  {p.featured_credits > 0 && <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> {p.featured_credits} featured credits/mo</li>}
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> {p.analytics_access} analytics</li>
                  {p.priority_support && <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Priority support</li>}
                </ul>
                <button onClick={() => subscribe(p.key)} disabled={busy === p.key || active} className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full ${active ? "bg-slate-100 text-[#252A34]/60" : "bg-[#165a71] text-white"} px-4 py-2 text-sm font-medium disabled:opacity-50`} data-testid={`plan-subscribe-${p.key}`}>
                  {active ? "Current plan" : busy === p.key ? "Redirecting…" : (price === 0 ? "Choose Free" : "Subscribe (Stripe test)")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardShell>
  );
}
